/**
 * Tipos del dominio Afiliado y entidades relacionadas.
 * Alineados con el schema SQL.
 */

export type EstadoAfiliado = 'activo' | 'inactivo' | 'baja';
export type TipoDocumento = 'DNI' | 'LE' | 'CE';
export type SexoTipo = 'M' | 'F' | 'X';
export type EstadoCivil =
  | 'soltero'
  | 'casado'
  | 'divorciado'
  | 'viudo'
  | 'conviviente';

export interface Afiliado {
  id: string;
  cuil: string;
  tipo_documento: TipoDocumento;
  numero_documento: string;
  apellido: string;
  nombre: string;
  sexo: SexoTipo | null;
  fecha_nacimiento: string | null;
  estado_civil: EstadoCivil | null;
  telefono: string | null;
  email: string | null;
  domicilio: string | null;
  provincia_id: string | null;
  localidad_id: string | null;
  observaciones: string | null;
  fecha_alta_sistema: string;
  estado: EstadoAfiliado;
  created_at: string;
  updated_at: string;
}

export type AfiliadoCreatePayload = Omit<Afiliado, 'id' | 'created_at' | 'updated_at'>;
export type AfiliadoUpdatePayload = Partial<AfiliadoCreatePayload>;

export interface AfiliadosFilter {
  search?: string;
  estado?: EstadoAfiliado | 'todos';
  page?: number;
  pageSize?: number;
  /**
   * Acota el listado a los afiliados con relación laboral ACTIVA con esta
   * empresa. Se usa cuando el contexto es de empresa (admin_empresa o admin
   * general impersonando). Si es null/undefined: padrón completo (#494).
   */
  empresaId?: string | null;
}

export interface AfiliadosResult {
  data: Afiliado[];
  count: number;
  page: number;
  pageSize: number;
}

// ===== Grupo Familiar =====

export interface MiembroFamiliar {
  id: string;
  afiliado_id: string;
  nombre: string;
  apellido: string;
  dni: string | null;
  parentesco_id: string | null;
  fecha_nacimiento: string | null;
  obra_social: string | null;
  observaciones: string | null;
  created_at: string;
  updated_at: string;
}

export type MiembroFamiliarCreatePayload = Omit<
  MiembroFamiliar,
  'id' | 'created_at' | 'updated_at'
>;
export type MiembroFamiliarUpdatePayload = Partial<MiembroFamiliarCreatePayload>;

// ===== Aportes Extra =====

export interface AporteExtra {
  id: string;
  afiliado_id: string;
  concepto_aporte_id: string;
  porcentaje_override: number | null;
  fecha_desde: string;
  fecha_hasta: string | null;
  observaciones: string | null;
  asignado_por: string | null;
  created_at: string;
  updated_at: string;
}

export type AporteExtraCreatePayload = Omit<
  AporteExtra,
  'id' | 'created_at' | 'updated_at'
>;
export type AporteExtraUpdatePayload = Partial<AporteExtraCreatePayload>;

/** Aporte extra "enriquecido" con el concepto base para mostrar en UI */
export interface AporteExtraConConcepto extends AporteExtra {
  concepto: {
    codigo: string;
    descripcion: string;
    porcentaje: number;
  };
}

// ===== Catalogos =====

export interface ConceptoAporte {
  id: string;
  codigo: string;
  descripcion: string;
  porcentaje: number;
  aplica_afiliados: boolean;
  aplica_no_afiliados: boolean;
  opcional: boolean;
  activo: boolean;
}

export interface Parentesco {
  id: string;
  descripcion: string;
}

// ===== Helpers de presentacion =====

export const ESTADO_AFILIADO_LABELS: Record<EstadoAfiliado, string> = {
  activo: 'Activo',
  inactivo: 'Inactivo',
  baja: 'Baja',
};

export const ESTADO_AFILIADO_COLORS: Record<EstadoAfiliado, 'success' | 'default' | 'error'> = {
  activo: 'success',
  inactivo: 'default',
  baja: 'error',
};

export const TIPO_DOC_LABELS: Record<TipoDocumento, string> = {
  DNI: 'DNI',
  LE: 'LE (Libreta Enrolamiento)',
  CE: 'CE (Cedula de Extranjero)',
};

export const SEXO_LABELS: Record<SexoTipo, string> = {
  F: 'Femenino',
  M: 'Masculino',
  X: 'Otro / Sin especificar',
};

export const ESTADO_CIVIL_LABELS: Record<EstadoCivil, string> = {
  soltero: 'Soltero/a',
  casado: 'Casado/a',
  divorciado: 'Divorciado/a',
  viudo: 'Viudo/a',
  conviviente: 'Conviviente',
};

export const ESTADO_AFILIADO_OPTIONS = Object.entries(ESTADO_AFILIADO_LABELS).map(
  ([value, label]) => ({ value: value as EstadoAfiliado, label })
);
export const TIPO_DOC_OPTIONS = Object.entries(TIPO_DOC_LABELS).map(
  ([value, label]) => ({ value: value as TipoDocumento, label })
);
export const SEXO_OPTIONS = Object.entries(SEXO_LABELS).map(([value, label]) => ({
  value: value as SexoTipo,
  label,
}));
export const ESTADO_CIVIL_OPTIONS = Object.entries(ESTADO_CIVIL_LABELS).map(
  ([value, label]) => ({ value: value as EstadoCivil, label })
);
